import { useState, useEffect } from 'react'

/* global Office */

type ColorScheme = 'light' | 'dark'

/**
 * Determines if a hex color is dark by calculating its perceived brightness.
 * Uses the luminance formula: (0.299*R + 0.587*G + 0.114*B)
 */
function isDarkColor(hex: string): boolean {
  const color = hex.replace('#', '')
  const r = Number.parseInt(color.substring(0, 2), 16)
  const g = Number.parseInt(color.substring(2, 4), 16)
  const b = Number.parseInt(color.substring(4, 6), 16)
  const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255
  return brightness < 0.5
}

/**
 * Reads the Outlook theme from Office.context.officeTheme, or null when there is no
 * Office host (dev in a plain browser) or the host doesn't expose a theme.
 */
function readOfficeTheme(): ColorScheme | null {
  try {
    const officeTheme = Office?.context?.officeTheme
    if (officeTheme?.bodyBackgroundColor) {
      return isDarkColor(officeTheme.bodyBackgroundColor) ? 'dark' : 'light'
    }
  } catch {
    // Office API not available
  }
  return null
}

function prefersDark(): boolean {
  return globalThis.matchMedia('(prefers-color-scheme: dark)').matches
}

/**
 * Hook that detects and follows Outlook's dark/light theme.
 * Outlook's theme wins whenever the host exposes one — including a dev build sideloaded
 * into Outlook, where the OS preference says nothing about the theme the user picked in
 * Outlook. Without one, DEV falls back to the OS prefers-color-scheme and prod to light.
 */
export function useOfficeTheme(): ColorScheme {
  const [colorScheme, setColorScheme] = useState<ColorScheme>(() => {
    const officeTheme = readOfficeTheme()
    if (officeTheme) return officeTheme
    if (import.meta.env.DEV) return prefersDark() ? 'dark' : 'light'
    return 'light'
  })

  useEffect(() => {
    if (readOfficeTheme()) {
      try {
        Office.context.mailbox.addHandlerAsync(Office.EventType.OfficeThemeChanged, () => {
          setColorScheme(readOfficeTheme() ?? 'light')
        })
      } catch {
        // Event not supported in this Outlook version
      }
      return
    }

    if (import.meta.env.DEV) {
      // No Office theme in dev — follow OS theme changes instead
      const mediaQuery = globalThis.matchMedia('(prefers-color-scheme: dark)')
      const handler = (e: MediaQueryListEvent) => {
        setColorScheme(e.matches ? 'dark' : 'light')
      }
      mediaQuery.addEventListener('change', handler)
      return () => mediaQuery.removeEventListener('change', handler)
    }
  }, [])

  return colorScheme
}
